import { skipToken } from '@reduxjs/toolkit/dist/query';
import { DEFAULT_PAGINATION_SIZE } from '../apiConstants';
import { apiSlice } from '../apiSlice';
import { DeleteResponse } from '../schemas/apiResponse';
import { PaginatedArgs, PaginatedResponse } from '../schemas/pagination';
import { RowSide } from '../schemas/rowSide';
import { Scan } from '../schemas/scan';
import { filterParamsAdapter } from '../utils/filterParamsAdapter';
import { providesList } from '../utils/providesList';

type ScanFilterParams = {
  id__in?: string[];
  name__like?: string;
  scan_date__gte?: string;
  scan_date__lte?: string;
  block__id__in?: string[];
  block__thru_date__isnull?: boolean;
  ranch__id__in?: string[];
  ranch__name__like?: string[];
  customer__id__in?: string[];
  customer__short_code__in?: string[];
  collection__id__in?: string[];
  search?: string;
  order_by?: string;
};

const SCANS_PATH = '/scans';
const PAGINATED_SCANS_PATH = '/v2/scans';

const scanEndpoints = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllScans: builder.query<Scan[], ScanFilterParams | void>({
      queryFn: async (arg, api, extra, baseQuery) => {
        // Initial request to determine the number of pages
        const initialRes = await baseQuery({
          url: PAGINATED_SCANS_PATH,
          params: {
            ...arg,
            page: 1,
            size: DEFAULT_PAGINATION_SIZE,
          },
        });
        if (initialRes.error) return { error: initialRes.error };
        const initialData = initialRes.data as PaginatedResponse<Scan>;
        const totalPages = initialData.pages;

        // Generate all page requests at once after knowing total pages
        const pageRequests = [];
        for (let page = 2; page <= totalPages; page++) {
          pageRequests.push(
            baseQuery({
              url: PAGINATED_SCANS_PATH,
              params: {
                ...arg,
                page,
                size: DEFAULT_PAGINATION_SIZE,
              },
            })
          );
        }

        // Wait for all page requests to complete
        const results = await Promise.all(pageRequests);
        let allScans = [...initialData.items];
        results.forEach((result) => {
          if (!result.error) {
            const data = result.data as PaginatedResponse<Scan>;
            allScans = allScans.concat(data.items);
          }
        });

        allScans.sort((a, b) => b.scanDate.localeCompare(a.scanDate));

        return { data: allScans };
      },
      providesTags: (result) => providesList(result, 'Scan'),
    }),
    getScans: builder.query<
      PaginatedResponse<Scan>,
      ScanFilterParams & PaginatedArgs
    >({
      query: (arg) => {
        const params = arg ? filterParamsAdapter(arg) : {};
        return {
          url: '/v2/scans',
          method: 'GET',
          params: {
            ...params,
            page: arg.page || 1,
            size: arg.size || DEFAULT_PAGINATION_SIZE,
          },
        };
      },
      providesTags: (result) => providesList(result?.items, 'Scan'),
    }),
    getScan: builder.query<Scan, string>({
      query: (scanId) => `${SCANS_PATH}/${scanId}`,
      providesTags: (result, error, id) => [{ type: 'Scan', id }],
    }),
    getRowSides: builder.query<RowSide[], string>({
      query: (scanId) => `${SCANS_PATH}/${scanId}/rowsides`,
      providesTags: (result) => providesList(result, 'RowSide'),
    }),
    deleteScan: builder.mutation<DeleteResponse, string>({
      query(id) {
        return {
          url: `${SCANS_PATH}/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, id) => [{ type: 'Scan', id }],
    }),
  }),
});

/**
 * Wrapper around useGetScanQuery that skips if scanId is not provided
 * @param scanId the id of the scan
 * @param queryOptions options to pass to useGetScanQuery, including additional skip condition(s)
 * @returns query result
 */
export const useGetScanByIdQuery = (
  scanId: string | undefined,
  queryOptions?: {
    skip?: boolean;
    pollingInterval?: number;
    refetchOnMountOrArgChange?: number;
  }
) => {
  const { useGetScanQuery } = scanEndpoints;

  const queryResult = useGetScanQuery(scanId ?? skipToken, {
    ...queryOptions,
    skip: !scanId || queryOptions?.skip,
  });

  return queryResult;
};

export const {
  useGetAllScansQuery,
  useGetScansQuery,
  useGetRowSidesQuery,
  useDeleteScanMutation,
} = scanEndpoints;
